import { defineStore, StoreDefinition } from 'pinia';
import { ref, computed } from '@vue/composition-api';
import { type ComputedRef } from '@vue/composition-api';
import { useComputed } from 'common-module/src/util/useComputed';
import { DISABLED_PERMISSION } from 'common-module/src/config/permission';

// 超级权限，拥有所有权限
export const SUPER_PERMISSION = '-1';

// 忽略权限，不进行权限校验
const IGNORE_PERMISSION = '*';

type ResourcePermissionStoreDefinition<T> = StoreDefinition<string, {
}, {
  resources: ComputedRef<string[]>;
  hasSuperPermission: ComputedRef<boolean>;
}, {
  setResources: (newResources?: T) => void;
  hasPermission: (needPermissions?: string[], every?: boolean) => boolean;
  hasSelfPermission: (needPermissions?: string[], every?: boolean) => boolean;
  hasPermissionComputed: (needPermissions?: string[], every?: boolean) => ComputedRef<boolean>;
  hasSelfPermissionComputed: (needPermissions?: string[], every?: boolean) => ComputedRef<boolean>;
  refreshResource: <Args extends unknown[]>(...args: Args) => Promise<void>;
}>;
export type ResourcePermissionOptions<T> = {
  // 加载权限资源
  load?: (...args: any[]) => Promise<T> | T;
  // 哪些属于超级权限
  superPermission?: string[];
  // 解析资源
  parse?: (resources?: T) => string[];
  dependencies?: ResourcePermissionStoreDefinition<T>[];
}

export const hasAccess = (resources: string[], need?: string[], every?: boolean, isSuper?: (resources: string[]) => boolean) => {
  if (!need?.length) {
    return true;
  }
  if (!resources?.length) {
    return false;
  }
  isSuper = isSuper || ((resources: string[]) => resources.includes(SUPER_PERMISSION));
  // 拥有超级权限 || 需要的为忽略权限 直接返回true
  if ((isSuper(resources) && !need.includes(DISABLED_PERMISSION)) || need.includes(IGNORE_PERMISSION)) {
    return true;
  }

  if (every) {
    return need.every((permission) => resources.includes(permission));
  }
  return need.some((permission) => resources.includes(permission));
};

/**
 * 创建一个通用的资源权限校验store
 * @param name
 * @param opts
 */
export const createResourcePermissionStore = <T = string[]>(name: string, opts?: ResourcePermissionOptions<T>) => {
  const { load, superPermission, parse, dependencies: _dependencies } = opts || {};
  const superPermissions = superPermission || [SUPER_PERMISSION];
  const doParseResource = parse || ((resources?: T) => (resources || []) as string[]);
  return defineStore(name, () => {
    const dependencies = _dependencies?.map(d => d());
    // state
    const selfResources = ref<string[]>([]);

    // computed
    const resources = computed(() => selfResources.value.concat(...dependencies?.map(d => d.resources) || []));
    const _isSuper = (resources: string[]) => superPermissions.some(sp => resources.includes(sp));
    const hasSuperPermission = computed(() => _isSuper(resources.value));

    // actions
    const setResources = (newResources?: T) => {
      selfResources.value = doParseResource(newResources);
    };

    const hasPermission = (needPermissions?: string[], every?: boolean) => {
      return hasAccess(resources.value, needPermissions, every, _isSuper);
    };

    const hasSelfPermission = (needPermissions?: string[], every?: boolean) => {
      return hasAccess(selfResources.value, needPermissions, every, _isSuper);
    };

    const hasPermissionComputed = useComputed(hasPermission);

    const hasSelfPermissionComputed = useComputed(hasSelfPermission);

    const refreshResource = async <Args extends unknown[]>(...args: Args) => {
      const newResources = await load?.(args);
      setResources(newResources);
    };

    return {
      resources,
      hasSuperPermission,
      hasPermissionComputed,
      hasSelfPermissionComputed,
      setResources,
      hasPermission,
      hasSelfPermission,
      refreshResource
    };
  });
};
